import { FormSectionHandler } from '../entity.handler';
import { AssetResponse, AssetUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { AssetGroupingService } from 'app/services';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { IdNameComboValueHandlers, ComboField, EntityUtils } from '../utils';


interface EditorValues {
     [key: string]: any; 
}

export class AssetCategoriesSectionHandler implements FormSectionHandler<AssetResponse, EditorValues> {

    type = 'form';
    icon = 'sitemap';
    bulk = true;

    title = this.translate('TITLE'); 
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private grouping: AssetGroupingService
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ASSET.CATEGORIES.${key}`, values);
    }

    applyUpdate(entity: AssetResponse, payload: AssetUpdateRequest): Promise<AssetResponse> {
        for (let i = 0; i < payload.categories.length; i++) {
            // The API requires categories to be located at a specific index in an array to update them correctly.
            // We have to set any undefined entries in the array to their previous values to prevent them being lost.
            // We use "null" to signify intentional removal, so here we have to check against "undefined".
            if (payload.categories[i] === undefined) {
                payload.categories[i] = entity.categories[i];
            }
        }
        return this.admin.applyUpdate(api => api.entities.updateAsset(entity.id, payload));
    }

    mapEntityToEditor(entity: AssetResponse): Promise<EditorValues> {
        const result = {};
        (entity.categories || []).forEach((cat, index) => {
            result[`category${index}`] = cat;
        });
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: AssetUpdateRequest, values: EditorValues): Promise<AssetUpdateRequest> {
        Object.keys(values).forEach(key => {
            if (key.indexOf('category') === 0) {
                const idx = parseFloat(key.replace('category', ''));
                entity.categories = entity.categories || [];
                if (values[key]) {
                    entity.categories[idx] = values[key];
                } else {
                    // user has chosen to remove this category, set to null to signify intent
                    entity.categories[idx] = null;
                }
            }
        });
        return Promise.resolve(entity);
    }

    getFields(entity: AssetResponse): Promise<FormBuilderField[]> {
        return Promise.all([
            this.grouping.getAssetCategories(this.ownerId),
        ]).then(([assetCategories]) => {
            return assetCategories.filter(x => x.parent === 'root').map(category => {
                const items = assetCategories.filter(x => x.index === category.index && x.parent !== 'root');
                return ComboField(`category${category.index}`, category.name, false, EntityUtils.treeToComboValues(items), IdNameComboValueHandlers);
            });
        }).then(fields => EntityUtils.prepareBulk(!entity, ['category0', 'category1', 'category2', 'category3', 'category4'], fields));
    }


    mapValidationErrors(errors: { [field: string]: string }): { [field: string]: string } {
        const result = {};
        Object.keys(errors).forEach(field => {
            let prop = field;
            if (field.startsWith('categories.')) {
                prop = `category${field.replace('categories.', '')}`;
            } 
            result[prop] = result[prop] || [];
            result[prop].push(errors[field]);
    });
        return Object.keys(result).reduce((obj, key) => { obj[key] = result[key].join(', '); return obj; }, {});
    }

}
0.
